/**
 * Panneau probabilités (specs/01 §7) : top 10 des chances de titre, barres +
 * %, delta depuis l'avant-tournoi, lien méthodologie.
 */
import modelParams from "../../data/model-params.json";
import gkRatings from "../../data/gk-ratings.json";
import { teams } from "../../app/data.ts";
import type { SimulationOutput } from "../../engine/montecarlo.ts";
import { esc, flagSpan, teamName } from "../html.ts";
import { fmtPct } from "../../app/format.ts";
import { t } from "../../app/i18n.ts";

const PRE: Record<string, number> = modelParams.preTournamentTitleProbs;
const ADJUSTED = new Set(teams.filter((t) => (t.eloFlags as string[]).includes("adjusted")).map((t) => t.id));

export interface ProbsMeta {
  readonly nSims: number;
  readonly elapsedMs: number;
  readonly computedAt: number;
  readonly running: boolean;
  /** true = calculé par le serveur (poussé) ; false = worker du navigateur. */
  readonly server?: boolean;
}

export function renderProbsPanel(el: HTMLElement, mc: SimulationOutput | null, meta: ProbsMeta | null): void {
  const header = `<h2 class="sec-title">${t("probs.title")} <span class="count">${t("probs.modelTag")}</span></h2>`;
  if (!mc) {
    el.innerHTML = `${header}<div class="card note">${meta?.running ? t("probs.running") : t("probs.waiting")}</div>`;
    return;
  }
  const top = Object.entries(mc.titleProb).slice(0, 10);
  const maxP = top[0]?.[1] ?? 1;
  el.innerHTML = `
    ${header}
    <div class="card probs">
      ${top
        .map(([code, p]) => {
          const pre = PRE[code];
          const nowPct = 100 * p;
          const delta =
            pre !== undefined
              ? nowPct > pre + 0.05
                ? `<span class="delta up" title="${t("probs.preTournamentTitle", { pct: pre.toFixed(1).replace(".", ",") })}">▲</span>`
                : nowPct < pre - 0.05
                  ? `<span class="delta down" title="${t("probs.preTournamentTitle", { pct: pre.toFixed(1).replace(".", ",") })}">▼</span>`
                  : `<span class="delta flat">=</span>`
              : `<span class="delta flat" title="${t("probs.preBelowThreshold")}">·</span>`;
          const preLabel = pre !== undefined ? `${pre.toFixed(1).replace(".", ",")} %` : t("probs.preLabelBelow");
          return `
        <div class="prob-row" title="${esc(t("probs.rowTitle", { team: teamName(code), preLabel }))}">
          <span class="prob-team">${flagSpan(code)}${ADJUSTED.has(code) ? "*" : ""}</span>
          <span class="prob-bar"><span class="prob-fill" style="width:${(100 * p) / maxP}%"></span></span>
          <span class="prob-vals"><span class="dim">${preLabel} →</span> ${esc(fmtPct(p))} ${delta}</span>
        </div>`;
        })
        .join("")}
      <div class="probs-meta dim">
        ${meta ? `${t("probs.metaDraws", { n: meta.nSims.toLocaleString("fr-FR") })} · ${meta.server ? t("probs.metaServer") : t("probs.metaBrowser", { ms: Math.round(meta.elapsedMs) })} · ${t("probs.metaSeed", { seed: esc(mc.seed) })}` : ""}
        ${meta?.running ? ` ${t("proj.picker.recalc")}` : ""}
      </div>
      <details class="metho">
        <summary>${t("method.summary")}</summary>
        <div class="note">
          <p>${t("method.elo")}</p>
          <p>${t("method.goals", { n: Object.keys(gkRatings.ratings).length })}</p>
          <p>${t("method.standings")}</p>
        </div>
      </details>
    </div>`;
}
